import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAllUpcomingEvents, getEventTypes } from 'utility/requests';
import { Trans } from '@lingui/macro';
import * as GA4 from 'analytics/ga4';

import useDebouncedEffect from 'hooks/useDebouncedEffect';
import MainAppBar from 'components/MainAppBar';
import SearchBar from 'components/SearchBar';
import EventListItem from 'components/EventListItem';
import EventTypeFilter from 'components/EventTypeFilter';
import { Button } from 'components/ui/button';

const PER_PAGE_OPTIONS = [25, 50, 75, 100];
const DEFAULT_PER_PAGE = 50;

function parsePerPage(raw) {
    const n = parseInt(raw || '', 10);
    return PER_PAGE_OPTIONS.includes(n) ? n : DEFAULT_PER_PAGE;
}

function parsePage(raw) {
    const n = parseInt(raw || '1', 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
}

export default function LandingPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const query = searchParams.get('query') || '';
    const type = searchParams.get('type') || '';
    const perPage = parsePerPage(searchParams.get('per'));
    const pageFromUrl = parsePage(searchParams.get('page'));

    const [events, setEvents] = useState({
        values: null,
        total: 0,
        loading: true,
        error: false,
        success: false,
    });
    const [eventTypes, setEventTypes] = useState([]);

    useEffect(() => {
        GA4.pageView('Landing Page');
    }, []);

    useEffect(() => {
        getEventTypes()
            .then(setEventTypes)
            .catch(() => { });
    }, []);

    const fetchEvents = useCallback((q, t, page, per) => {
        setEvents((prev) => ({ ...prev, loading: true }));

        const params = { page, per };
        if (q) params.query = q;
        if (t) params.type = t;

        getAllUpcomingEvents(params)
            .then(({ events: rows, total }) =>
                setEvents({
                    values: rows,
                    total,
                    loading: false,
                    error: false,
                    success: true,
                }),
            )
            .catch(() => {
                setEvents({
                    values: null,
                    total: 0,
                    loading: false,
                    error: true,
                    success: false,
                });
            });
    }, []);

    useDebouncedEffect(
        () => fetchEvents(query, type, pageFromUrl, perPage),
        query ? 300 : 0,
        [query, type, pageFromUrl, perPage, fetchEvents],
    );

    const pagination = useMemo(() => {
        const totalCount = events.success ? events.total : 0;
        const totalPages = totalCount === 0 ? 1 : Math.ceil(totalCount / perPage);
        const currentPage = Math.min(pageFromUrl, totalPages);
        const pageRows = events.success && Array.isArray(events.values) ? events.values : [];
        const displayStart =
            totalCount === 0 || pageRows.length === 0
                ? 0
                : (currentPage - 1) * perPage + 1;
        const displayEnd =
            totalCount === 0 || pageRows.length === 0
                ? 0
                : (currentPage - 1) * perPage + pageRows.length;
        return {
            totalCount,
            totalPages,
            currentPage,
            pageRows,
            displayStart,
            displayEnd,
        };
    }, [events.success, events.total, events.values, perPage, pageFromUrl]);

    useEffect(() => {
        if (!events.success) {
            return;
        }
        const totalCount = events.total;
        const totalPages = totalCount === 0 ? 1 : Math.ceil(totalCount / perPage);
        const clamped = Math.min(pageFromUrl, totalPages);
        if (clamped === pageFromUrl) {
            return;
        }
        const next = new URLSearchParams(searchParams);
        if (clamped <= 1) {
            next.delete('page');
        } else {
            next.set('page', String(clamped));
        }
        setSearchParams(next, { replace: true });
    }, [events.success, events.total, perPage, pageFromUrl, searchParams, setSearchParams]);

    const goToPage = (p) => {
        const next = new URLSearchParams(searchParams);
        if (p <= 1) {
            next.delete('page');
        } else {
            next.set('page', String(p));
        }
        setSearchParams(next, { replace: true });

        window.scrollTo(0, 0);
    };

    const handlePerPageChange = (e) => {
        const v = parseInt(e.target.value, 10);
        const next = new URLSearchParams(searchParams);
        if (v === DEFAULT_PER_PAGE) {
            next.delete('per');
        } else {
            next.set('per', String(v));
        }
        next.delete('page');
        setSearchParams(next, { replace: true });
    };

    const handleSearchChange = (value) => {
        const next = new URLSearchParams(searchParams);

        if (value) {
            next.set('query', value);
        } else {
            next.delete('query');
        }
        next.delete('page');

        setSearchParams(next, { replace: true });
    };

    const handleTypeChange = (typeId) => {
        const next = new URLSearchParams(searchParams);

        if (typeId) {
            next.set('type', typeId);
        } else {
            next.delete('type');
        }
        next.delete('page');

        setSearchParams(next, { replace: true });
    };

    return (
        <div className="min-h-full text-foreground">
            <MainAppBar />

            <div className="max-w-screen-xl mx-auto p-4 lg:pt-8">
                {/* Hero Section */}
                <div className="text-center py-12">
                    <h1 className="text-4xl md:text-5xl font-extrabold font-inter text-foreground mb-3">
                        <Trans>Upcoming Events</Trans>
                    </h1>
                    <p className="text-[rgba(185,191,198,1)] text-lg font-inter">
                        <Trans>Find your event and grab your tickets.</Trans>
                    </p>
                </div>

                {/* Search & Filters - Sticky */}
                <div className="sticky top-0 z-30 -mx-4 px-4 py-4 bg-background/80 backdrop-blur-md">
                    <div className="flex flex-col items-center">
                        <SearchBar value={query} onChange={handleSearchChange} />

                        <div className="mt-5">
                            <EventTypeFilter
                                eventTypes={eventTypes}
                                selectedType={type}
                                onTypeChange={handleTypeChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Error State */}
                {events.error && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <p className="text-lg text-foreground">
                            <Trans>Sorry we couldn't find any events</Trans>
                        </p>
                    </div>
                )}

                {/* Empty State */}
                {events.success && events.total === 0 && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <p className="text-muted-foreground text-center">
                            <Trans>There are no scheduled events for sale at the moment</Trans>
                        </p>
                    </div>
                )}

                {/* Event Grid */}
                <div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6"
                    style={{ opacity: events.loading ? 0.5 : 1, transition: 'opacity 300ms ease' }}
                >
                    {pagination.pageRows.map((e) => (
                        <EventListItem key={e.event_id} event={e} includeHostLink />
                    ))}
                </div>

                {events.success && pagination.totalCount > 0 && (
                    <div className="mt-10 gap-6 flex flex-col md:flex-row items-center">
                        <label className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="shrink-0">
                                <Trans>Events per page</Trans>
                            </span>
                            <select
                                className="h-9 min-w-[4.5rem] rounded-md border border-border bg-card px-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                value={perPage}
                                onChange={handlePerPageChange}
                            >
                                {PER_PAGE_OPTIONS.map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <p className="text-center text-sm text-muted-foreground">
                            <Trans>
                                Showing {pagination.displayStart}–{pagination.displayEnd} of{' '}
                                {pagination.totalCount}
                            </Trans>
                        </p>

                        <div className="flex items-center gap-2 md:ml-auto">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={pagination.currentPage <= 1 || events.loading}
                                onClick={() => goToPage(pagination.currentPage - 1)}
                            >
                                <Trans>Previous</Trans>
                            </Button>
                            <span className="px-1 text-sm text-foreground tabular-nums">
                                <Trans>
                                    Page {pagination.currentPage} of {pagination.totalPages}
                                </Trans>
                            </span>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={
                                    pagination.currentPage >= pagination.totalPages || events.loading
                                }
                                onClick={() => goToPage(pagination.currentPage + 1)}
                            >
                                <Trans>Next</Trans>
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
